import { useState } from "react";
import { api, ApiError, type Bucket, type Child, type Goal } from "../lib/api";
import { formatBp, formatCents } from "../lib/money";
import { CountItOut } from "./CountItOut";
import { jarHue } from "./Jar";

/* What a tapped jar opens into.
 *
 * The jars themselves stay wordless -- a picture of a container with coins in
 * it. Everything a number-shaped question needs (how much exactly, where it
 * came from, what I can do with it) lives here, one tap away, and only for the
 * jar actually tapped. That is what keeps the home screen readable for a
 * four-year-old while still giving an eight-year-old somewhere to go.
 */

const SPECIAL = "special";

/* Quick amounts, because a child this age should not have to type.
 * Filtered to what is actually in the jar, so the buttons never offer a move
 * that will bounce. */
const QUICK = [25, 50, 100, 500];

export function JarDetail({
  child,
  bucket,
  buckets,
  goal,
  onClose,
  onMoved,
  onAskCash,
  onLogSpend,
}: {
  child: Child;
  bucket: Bucket;
  buckets: Bucket[];
  goal: Goal | null;
  onClose: () => void;
  onMoved: (message: string) => void;
  onAskCash: (bucketId: string) => void;
  onLogSpend: (bucketId: string) => void;
}) {
  const hue = jarHue(bucket.kind);
  const isSpecial = bucket.kind === SPECIAL;
  const policy = child.specialPolicy;

  /* null, or which way money is about to go. The Special jar is the only
     panel that offers both, and they are the same form with the from/to
     swapped -- one piece of state rather than two booleans that could
     disagree. */
  const [moving, setMoving] = useState<null | "in" | "out">(null);
  const [amount, setAmount] = useState(0);
  const [other, setOther] = useState(
    () => buckets.find((b) => b.kind !== SPECIAL)?.id ?? "",
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Out of Special is the gated direction; into it is never gated.
  const canTakeOut = isSpecial && policy !== "parent_only" && bucket.balanceCents > 0;
  const asksFirst = isSpecial && policy === "ask_first";
  /* Putting money by used to hang off every OTHER jar's panel, which put a
     button about the Special jar on four screens that were not about it. It
     lives on the Special jar itself now: one place, next to taking it back
     out, and the jar the money comes from is a choice inside the form. */
  const sources = buckets.filter(
    (b) => b.kind !== SPECIAL && b.balanceCents > 0);
  const canPutIn = isSpecial && sources.length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body =
        moving === "in"
          ? { fromBucketId: other, toBucketId: bucket.id, amountCents: amount }
          : { fromBucketId: bucket.id, toBucketId: other, amountCents: amount };
      const r = await api.post<{ status: string }>(
        `/children/${child.id}/transfer`, body);
      setMoving(null);
      setAmount(0);
      onMoved(
        r.status === "requested"
          ? "Asked! A grown-up will say yes or no."
          : "Moved it.",
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  /* Whichever jar is being emptied, which is not this one when money is
     coming IN. Getting this wrong offers a child $5.00 out of a jar holding
     forty cents and lets the server be the one to say no. */
  const fromCents =
    moving === "in"
      ? (buckets.find((b) => b.id === other)?.balanceCents ?? 0)
      : bucket.balanceCents;
  const quick = QUICK.filter((c) => c <= fromCents);
  const jarGoal = goal && goal.bucketKey === bucket.key ? goal : null;

  return (
    <section
      className="card"
      style={{
        marginTop: 14,
        borderColor: hue.line,
        borderWidth: 3,
        background: hue.soft,
      }}
    >
      <div style={{
        display: "flex", alignItems: "baseline",
        justifyContent: "space-between", gap: 10, marginBottom: 2 }}>
        <h2 style={{ fontSize: 22, color: hue.deep }}>{bucket.name}</h2>
        <button onClick={onClose}
                style={{ minHeight: 44, fontSize: 14, padding: "0 14px" }}>
          Close
        </button>
      </div>

      <p className="money" style={{ margin: 0, fontSize: 34, fontWeight: 700 }}>
        {formatCents(bucket.balanceCents)}
      </p>

      <dl style={{ display: "grid", gap: 6, margin: "0 0 14px" }}>
        {isSpecial ? (
          <Fact
            label="Where it comes from"
            value="Only what you put in yourself"
          />
        ) : (
          bucket.percentBp != null && (
            <Fact
              label="Share of your allowance"
              value={formatBp(bucket.percentBp)}
            />
          )
        )}
        {bucket.walletCents > 0 && (
          <Fact label="Already in your pocket"
                value={formatCents(bucket.walletCents)} />
        )}
        {bucket.holdCents > 0 && (
          <Fact label="Waiting for a grown-up"
                value={formatCents(bucket.holdCents)} />
        )}
        {child.capabilities.sees_interest && bucket.interest?.enabled === true && (
          <Fact label="Earns interest" value="Yes — it grows on its own" />
        )}
        {jarGoal && (
          <Fact
            label={`Saving for ${jarGoal.name}`}
            value={`${formatCents(jarGoal.savedCents)} of ${formatCents(jarGoal.targetCents)}`}
          />
        )}
      </dl>

      {/* The same money, counted different ways. The jar on the shelf shows the
          fewest notes and coins; this is where "but my allowance came as five
          dollar bills" gets answered.

          Gated on the same switch as the jars themselves: a grown-up who turned
          the pictures off does not want a panel of drawn notes one tap further
          in. */}
      {child.showCashPictures && (
        <CountItOut cents={bucket.balanceCents} hue={hue.line} />
      )}

      {/* WHAT YOU CAN DO WITH THIS JAR */}
      {moving ? (
        <div style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {moving === "in"
              ? `How much do you want to put into ${bucket.name}?`
              : asksFirst
                ? "How much do you want to ask for?"
                : "How much do you want to take out?"}
          </p>

          <label style={{ display: "grid", gap: 4, fontSize: 14 }}>
            <span style={{ fontWeight: 600 }}>
              {moving === "in"
                ? "Which jar does it come from?"
                : "Which jar does it go to?"}
            </span>
            <select value={other} onChange={(e) => setOther(e.target.value)}>
              {(moving === "in" ? sources : buckets.filter((b) => b.kind !== SPECIAL))
                .map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
            </select>
          </label>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {quick.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setAmount(c)}
                aria-pressed={amount === c}
                className="money"
                style={{
                  minHeight: 52, padding: "0 16px", fontWeight: 700,
                  background: amount === c ? "var(--ink)" : "var(--paper)",
                  color: amount === c ? "var(--paper)" : "var(--ink)",
                  borderColor: "var(--ink)",
                }}
              >
                {formatCents(c)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setAmount(fromCents)}
              aria-pressed={amount === fromCents && amount > 0}
              style={{
                minHeight: 52, padding: "0 16px", fontWeight: 700,
                background:
                  amount === fromCents && amount > 0
                    ? "var(--ink)" : "var(--paper)",
                color:
                  amount === fromCents && amount > 0
                    ? "var(--paper)" : "var(--ink)",
                borderColor: "var(--ink)",
              }}
            >
              All of it
            </button>
          </div>

          {amount > fromCents && (
            <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
              There&rsquo;s only {formatCents(fromCents)} in there.
            </p>
          )}
          {error && (
            <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
              {error}
            </p>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={submit}
              disabled={busy || amount <= 0 || amount > fromCents}
              style={{ flex: 1, minHeight: 56 }}
            >
              {busy
                ? "…"
                : amount <= 0
                  ? "Pick an amount"
                  : asksFirst && moving === "out"
                    ? `Ask for ${formatCents(amount)}`
                    : `Move ${formatCents(amount)}`}
            </button>
            <button onClick={() => { setMoving(null); setAmount(0); }} disabled={busy}>
              Never mind
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {canPutIn && (
            <button
              /* Point the picker at a jar that actually has something in it.
                 Left on the default it can open on an empty jar, and the form
                 offers a child nothing to move without saying why. */
              onClick={() => {
                setOther(sources[0].id);
                setAmount(0);
                setMoving("in");
              }}
              style={{ minHeight: 56 }}
            >
              ✨ Put money in
            </button>
          )}
          {canTakeOut && (
            <button
              onClick={() => {
                setOther(buckets.find((b) => b.kind !== SPECIAL)?.id ?? "");
                setAmount(0);
                setMoving("out");
              }}
              style={{ minHeight: 56 }}
            >
              {asksFirst ? "🙋 Ask to take money out" : "↩️ Take money out"}
            </button>
          )}
          {isSpecial && policy === "parent_only" && (
            <p style={{ margin: 0, fontSize: 14, color: "var(--ink-2)" }}>
              You can put money in here any time. Ask a grown-up when you want
              to take some back out.
            </p>
          )}
          {isSpecial && bucket.balanceCents === 0 && sources.length === 0 && (
            <p style={{ margin: 0, fontSize: 14, color: "var(--ink-2)" }}>
              Nothing in here yet, and no money in your other jars to put in.
            </p>
          )}

          {child.capabilities.can_request_cashout && bucket.withdrawable &&
            bucket.balanceCents > 0 && (
            <button onClick={() => onAskCash(bucket.id)} style={{ minHeight: 56 }}>
              💵 Ask for this as real cash
            </button>
          )}
          {child.capabilities.can_log_spending && bucket.walletCents > 0 && (
            <button onClick={() => onLogSpend(bucket.id)} style={{ minHeight: 56 }}>
              🧾 I spent some of this
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <dt style={{ fontSize: 14, color: "var(--ink-2)" }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, fontWeight: 600, textAlign: "right" }}>
        {value}
      </dd>
    </div>
  );
}
